/**
 * Category filter dropdown (converted from the Webflow `.dropdown` on
 * products.html / detail_category.html). Rebuilt as a React-state dropdown
 * driven by GSAP — no Webflow JS, no UI library.
 *
 * Motion mirrors IX2 a-26 / a-27 (ANIMATIONS.md §7b):
 *   OPEN  → `.dropdown-list` display flex, opacity 0→1 (600ms);
 *           `.drop-down-arrow` rotate 0→180 (500ms).
 *   CLOSE → arrow 180→0 (500ms), list opacity 1→0 (600ms),
 *           then display:none after 700ms.
 *
 * The `.w--open` class is toggled on the toggle + list so the original Webflow
 * CSS (open background, border, `display:block`) applies; opacity/visibility
 * are animated on top. Selecting a category navigates to /category/[slug].
 */
import type { FC, KeyboardEvent } from 'react'
import type { Category } from '@/types'

import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'

import { gsap } from '@/lib/gsap'

interface SortDropdownProps {
    categories: Category[]
    label: string
    activeSlug?: string
    semiBold?: boolean
}

export const SortDropdown: FC<SortDropdownProps> = ({
    categories,
    label,
    activeSlug,
    semiBold = false
}) => {
    const router = useRouter()
    const [open, setOpen] = useState(false)

    const dropdownRef = useRef<HTMLDivElement>(null)
    const listRef = useRef<HTMLElement>(null)
    const arrowRef = useRef<HTMLImageElement>(null)
    const firstRun = useRef(true)

    /* GSAP open / close (a-26 / a-27) */
    useEffect(() => {
        const list = listRef.current
        const arrow = arrowRef.current
        if (!list || !arrow) return

        if (firstRun.current) {
            firstRun.current = false
            gsap.set(list, { display: 'none', opacity: 0 })
            gsap.set(arrow, { rotation: 0 })
            return
        }

        if (open) {
            gsap.set(list, { display: 'flex' })
            gsap.to(list, { opacity: 1, duration: 0.6, ease: 'power1.inOut' })
            gsap.to(arrow, { rotation: 180, duration: 0.5, ease: 'power1.inOut' })
        } else {
            gsap.to(arrow, { rotation: 0, duration: 0.5, ease: 'power1.inOut' })
            gsap.to(list, {
                opacity: 0,
                duration: 0.6,
                ease: 'power1.inOut',
                onComplete: () => gsap.set(list, { display: 'none' })
            })
        }
    }, [open])

    /* Close on outside click + route change */
    useEffect(() => {
        const onDocClick = (event: MouseEvent) => {
            if (!dropdownRef.current?.contains(event.target as Node)) setOpen(false)
        }

        const onRouteChange = () => setOpen(false)

        document.addEventListener('click', onDocClick)
        router.events.on('routeChangeStart', onRouteChange)

        return () => {
            document.removeEventListener('click', onDocClick)
            router.events.off('routeChangeStart', onRouteChange)
        }
    }, [router.events])

    const onToggleKey = (event: KeyboardEvent) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            setOpen((value) => !value)
        } else if (event.key === 'Escape') {
            setOpen(false)
        }
    }

    return (
        <div className='category-small-container no-margins'>
            <div ref={dropdownRef} className='dropdown category w-dropdown'>
                <div
                    className={`dropdown-toggle ${semiBold ? 'semi-bold ' : ''}category w-dropdown-toggle${open ? ' w--open' : ''}`}
                    role='button'
                    tabIndex={0}
                    aria-haspopup='menu'
                    aria-expanded={open}
                    onClick={() => setOpen((value) => !value)}
                    onKeyDown={onToggleKey}
                >
                    <div>{label}</div>
                    <img
                        ref={arrowRef}
                        src='/images/Drop-Down-Arrow.svg'
                        loading='lazy'
                        alt=''
                        className='drop-down-arrow'
                    />
                </div>
                <nav
                    ref={listRef}
                    className={`dropdown-list w-dropdown-list${open ? ' w--open' : ''}`}
                >
                    <div className='collection-list-wrapper w-dyn-list'>
                        <div role='list' className='w-dyn-items'>
                            {categories.map((category) => (
                                <div key={category.slug} role='listitem' className='collection-item w-dyn-item'>
                                    <Link
                                        href={`/category/${category.slug}`}
                                        aria-current={category.slug === activeSlug ? 'page' : undefined}
                                        className={`dropdown-link w-dropdown-link${category.slug === activeSlug ? ' w--current' : ''}`}
                                        onClick={() => setOpen(false)}
                                    >
                                        {category.name}
                                    </Link>
                                </div>
                            ))}
                        </div>
                    </div>
                </nav>
            </div>
        </div>
    )
}
